import type { FaqItem, FaqLayout, FaqProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const LAYOUT = ["single", "two-column"] as const;

function newItemId(): string {
  return `f-${Math.random().toString(36).slice(2, 8)}`;
}

export function FaqInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as FaqProps;
  const items = p.items ?? [];

  const setItems = (next: FaqItem[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([...items, { id: newItemId(), question: "New question", answer: "" }]);

  const updateItem = (id: string, patch: Partial<FaqItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <TextField
        label="Title"
        value={p.title ?? ""}
        onChange={(v) => update(widget.id, { title: v || undefined })}
      />
      <TextField
        label="Subtitle"
        value={p.subtitle ?? ""}
        onChange={(v) => update(widget.id, { subtitle: v || undefined })}
      />
      <SelectField
        label="Layout"
        value={p.layout ?? "single"}
        options={LAYOUT}
        onChange={(v) => update(widget.id, { layout: v as FaqLayout })}
      />
      <CheckboxField
        label="Only one answer open at a time"
        value={p.exclusive ?? true}
        onChange={(v) => update(widget.id, { exclusive: v })}
      />
      <CheckboxField
        label="Open the first item by default"
        value={p.defaultOpenFirst ?? false}
        onChange={(v) => update(widget.id, { defaultOpenFirst: v })}
      />
      <CheckboxField
        label="Emit schema.org FAQPage JSON-LD (SEO)"
        value={p.emitSchemaOrg ?? true}
        onChange={(v) => update(widget.id, { emitSchemaOrg: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Questions ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              title="Delete"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>

          <TextField
            label={`Q${i + 1}`}
            value={it.question}
            onChange={(v) => updateItem(it.id, { question: v })}
          />
          <TextField
            label="Answer"
            value={it.answer}
            multiline
            onChange={(v) => updateItem(it.id, { answer: v })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add question
        </button>
      </div>
    </>
  );
}
